import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "redux/actions/auth";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import cosmaLogo from "assets/images/logos/Cosmaupload.png";
import { useMaterialUIController, setLayout } from "context";

const ACCENT = "#c47800";

const S = {
  root: {
    fontFamily: "'Inter', sans-serif",
    background: `url(${bgImage}) center center / cover no-repeat`,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  box: {
    background: "#fff",
    borderRadius: "24px",
    padding: "48px 40px",
    boxShadow: "0 2px 24px rgba(196,120,0,0.12)",
    width: "100%",
    maxWidth: "480px",
  },
  back: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    color: ACCENT, fontSize: "0.83rem", fontWeight: 600,
    cursor: "pointer", border: "none", background: "none",
    fontFamily: "'Inter', sans-serif", marginBottom: "20px", padding: 0,
  },
  logo: { textAlign: "center", marginBottom: "20px" },
  logoImg: { height: "40px" },
  freeBadge: {
    display: "inline-block", background: `${ACCENT}15`,
    border: `1.5px solid ${ACCENT}`, color: ACCENT,
    fontSize: "0.72rem", fontWeight: 800, padding: "3px 12px",
    borderRadius: "100px", textTransform: "uppercase",
    letterSpacing: "0.08em", marginBottom: "12px",
  },
  h3: { fontSize: "1.55rem", fontWeight: 800, color: "#1a0a2e", margin: "0 0 6px 0" },
  sub: { color: "#7a6895", fontSize: "0.87rem", marginBottom: "22px", lineHeight: 1.6 },
  fieldWrap: { marginBottom: "16px" },
  label: {
    display: "block", fontSize: "0.76rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em",
    color: "#7a6895", marginBottom: "6px",
  },
  input: (focus) => ({
    width: "100%", padding: "13px 16px", background: "#fffbf0",
    border: `1.5px solid ${focus ? ACCENT : "rgba(196,120,0,0.18)"}`,
    borderRadius: "10px", color: "#1a0a2e", fontSize: "0.95rem",
    fontFamily: "'Inter', sans-serif", outline: "none",
    boxSizing: "border-box", transition: "border-color .2s",
  }),
  btn: {
    width: "100%", padding: "15px", borderRadius: "10px",
    fontSize: "1rem", fontWeight: 800, cursor: "pointer",
    border: "none", marginTop: "8px",
    background: `linear-gradient(135deg, ${ACCENT}, #a06000)`, color: "#fff",
    boxShadow: `0 4px 20px ${ACCENT}44`, fontFamily: "'Inter', sans-serif",
    transition: "opacity .2s",
  },
  freeBox: {
    background: `${ACCENT}0d`, border: `1.5px solid ${ACCENT}30`,
    borderRadius: "12px", padding: "16px 18px", marginBottom: "22px",
  },
  freeBoxTitle: { fontSize: "0.85rem", fontWeight: 800, color: "#8a5200", marginBottom: "8px" },
  freeBoxRow: { display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px", fontSize: "0.81rem", color: "#5a3d00", lineHeight: 1.5 },
  upgradeBox: {
    background: "#f3eeff", border: "1.5px solid rgba(123,94,167,0.25)",
    borderRadius: "12px", padding: "14px 16px", marginBottom: "22px",
    fontSize: "0.82rem", color: "#3d2d5a", lineHeight: 1.6,
  },
  upgradeLink: { color: "#7b5ea7", fontWeight: 700, textDecoration: "underline" },
  links: { marginTop: "20px", fontSize: "0.83rem", color: "#7a6895", textAlign: "center" },
  a: { color: ACCENT, textDecoration: "none", fontWeight: 600 },
  hint: { fontSize: "0.76rem", color: "#9a88b5", marginTop: "4px" },
  toast: {
    position: "fixed", bottom: "32px", left: "50%", transform: "translateX(-50%)",
    background: `linear-gradient(135deg, ${ACCENT}, #a06000)`, color: "#fff",
    padding: "12px 28px", borderRadius: "100px", fontWeight: 700,
    fontSize: "0.9rem", zIndex: 9999, whiteSpace: "nowrap",
  },
};

const CHECK = "✓";

function CommunityRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, uiDispatch] = useMaterialUIController();
  const { registerLoading } = useSelector((state) => state.auth);
  const [focused, setFocused] = useState(null);
  const [toast, setToast] = useState({ msg: "", show: false });
  const [pwError, setPwError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });

  useEffect(() => { setLayout(uiDispatch, "page"); }, []);

  const showToast = (msg) => {
    setToast({ msg, show: true });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === "confirmPassword" || e.target.name === "password") setPwError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setPwError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setPwError("Password must be at least 8 characters.");
      return;
    }
    showToast("Creating free account…");
    dispatch(actions.register({ ...form, type: "community" }));
  };

  const f = (name) => ({ onFocus: () => setFocused(name), onBlur: () => setFocused(null) });

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={S.root}>
        <div style={S.box}>
          <button style={S.back} onClick={() => navigate("/auth/register")}>
            ← Back to account types
          </button>

          <div style={S.logo}>
            <img src={cosmaLogo} alt="COSMA" style={S.logoImg} />
          </div>

          <span style={S.freeBadge}>Free — Community</span>
          <h3 style={S.h3}>Join the Community</h3>
          <p style={S.sub}>
            Play COSMA brain games for free. Upgrade any time to unlock the full portal.
          </p>

          <div style={S.freeBox}>
            <div style={S.freeBoxTitle}>What's included for free:</div>
            <div style={S.freeBoxRow}><span>{CHECK}</span> Play all COSMA brain & wellness games on the app</div>
            <div style={S.freeBoxRow}><span>{CHECK}</span> Access your game history in the app</div>
            <div style={S.freeBoxRow}><span>{CHECK}</span> Meditation & physical activity tracking</div>
          </div>

          <div style={S.upgradeBox}>
            <strong>Want full portal access?</strong> Progress charts, cognitive reports, and clinical dashboards require a paid subscription.{" "}
            <a
              href="https://cosmaapp.com/#pricing"
              target="_blank"
              rel="noopener noreferrer"
              style={S.upgradeLink}
            >
              View plans →
            </a>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={S.fieldWrap}>
              <label style={S.label}>Full Name</label>
              <input
                type="text" name="name" placeholder="Your full name" required
                value={form.name} onChange={handleChange}
                style={S.input(focused === "name")} {...f("name")}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Email Address</label>
              <input
                type="email" name="email" placeholder="you@example.com" required
                value={form.email} onChange={handleChange}
                style={S.input(focused === "email")} {...f("email")}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Password</label>
              <input
                type="password" name="password" placeholder="Min. 8 characters" required
                value={form.password} onChange={handleChange}
                style={S.input(focused === "password")} {...f("password")}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Confirm Password</label>
              <input
                type="password" name="confirmPassword" placeholder="Re-enter password" required
                value={form.confirmPassword} onChange={handleChange}
                style={S.input(focused === "confirmPassword")} {...f("confirmPassword")}
              />
              {pwError && <p style={{ ...S.hint, color: "#c0392b" }}>{pwError}</p>}
            </div>

            <button type="submit" disabled={registerLoading} style={{ ...S.btn, opacity: registerLoading ? 0.7 : 1 }}>
              {registerLoading ? "Creating Account…" : "Join for Free →"}
            </button>
          </form>

          <p style={S.links}>
            Already have an account?{" "}
            <a href="/auth/login" style={S.a}>Sign In →</a>
            &nbsp;|&nbsp;
            <a href="/auth/register" style={S.a}>Choose different type</a>
          </p>
        </div>
      </div>
      {toast.show && <div style={S.toast}>{toast.msg}</div>}
    </>
  );
}

export default CommunityRegister;
